import React, { useEffect, useRef, useState } from "react";
import * as m from "./lib/model";
import { qsbsBump } from "./components/room/Qsbs";
import { shareApi } from "./lib/api";
import { Logo, RecBadge } from "./components/shared";
import RollUpEconomics from "./components/RollUpEconomics";
import { BackLeverCard } from "./components/BackLeverCard";
import { RollUpTab } from "./components/room/RollUpTab";
import { ROOM_SECTIONS, ROOM_GROUPS } from "./components/ShareRoomModal";
import { FinancialsTab, SourcesUsesTab, ReturnsTab, SensitivityTab, MarketTab, RisksTab } from "./components/room/RoomTabs";
import { OpportunityTab, ThesisTab, PipelineTab, TeamTab, StructureTab } from "./components/room/Sections";
import { InvestTab, CommitBar } from "./components/room/InvestTab";
import { DocumentsTab } from "./components/room/Documents";
import { QATab } from "./components/room/QA";
import { Lock, RefreshCw, FileDown } from "lucide-react";
import { PrintDeck } from "./components/room/PrintDeck";
import { PassTab, DecideSwitch } from "./components/room/PassTab";

export { applyRoomMode } from "./lib/roomModel";
import { roomModel } from "./lib/roomModel";
import { DisclaimerModal, disclaimerKey } from "./components/room/Disclaimer";
import { ThemeToggle } from "./components/ThemeToggle";
import { GitMerge, Sparkles, Target, Globe2, BarChart3, PieChart, TrendingUp, Layers, ListChecks, Users, Network, ShieldAlert, FolderOpen, MessageCircleQuestion, HandCoins, XCircle } from "lucide-react";

const storeKey = (id) => `xyz_room_${id}`;

const SEC_ICON = {
  overview: Sparkles, thesis: Target, market: Globe2, financials: BarChart3, sourcesuses: PieChart, returns: TrendingUp,
  assumptions: Layers, pipeline: ListChecks, team: Users, rollup: Network, risks: ShieldAlert,
  documents: FolderOpen, qa: MessageCircleQuestion, invest: HandCoins, pass: XCircle,
};

export default function InvestorRoom({ shareId }) {
  const [pass, setPass] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(true);
  const [tab, setTab] = useState(null);
  const [rcase, setRcase] = useState("base");   // investors can stress the case themselves
  const [needsDisclaimer, setNeedsDisclaimer] = useState(false);
  const [printing, setPrinting] = useState(false);
  const tick = useRef({ tab: null, since: 0 });

  const enter = async (creds) => {
    const c = creds || { passcode: pass, name, email };
    if (!c.key && !c.sessionId) {
      if (!c.passcode) return;
      if (!(c.name || "").trim()) { setErr("Please enter your name"); return; }
    }
    setBusy(true); setErr("");
    try {
      const r = await shareApi.view(shareId, c);
      // Only the session id is kept — a personal key never lands in storage or the address bar.
      sessionStorage.setItem(storeKey(shareId), JSON.stringify({ sessionId: r.sessionId }));
      setName(r.viewer?.name || c.name || "");
      setEmail(r.viewer?.email || c.email || "");
      setData(r);
      setTab((t) => (t && r.sections.includes(t) ? t : r.sections[0]));
      setNeedsDisclaimer(!localStorage.getItem(disclaimerKey(shareId, r.viewer?.email || r.viewer?.name || "")));
    } catch (e) {
      sessionStorage.removeItem(storeKey(shareId));
      setErr(e?.response?.data?.detail || "Could not open the deal room");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    const url = new URL(window.location.href);
    const key = url.searchParams.get("k") || "";
    if (key) {
      url.searchParams.delete("k");
      window.history.replaceState({}, document.title, url.pathname + url.hash);
    }
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(storeKey(shareId)) || "null"); } catch { /* ignore */ }
    // A personal link or a live session opens straight away; otherwise show the passcode gate.
    if (key || saved?.sessionId) enter({ key, sessionId: saved?.sessionId || "" }).finally(() => setOpening(false));
    else setOpening(false);
  }, [shareId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Time-on-tab tracking: flush on tab change, every 20s, and on hide/unload.
  useEffect(() => {
    if (!data?.sessionId || !tab) return;
    const flush = () => {
      const t = tick.current;
      if (!t.tab || !t.since) return;
      const secs = (Date.now() - t.since) / 1000;
      if (secs >= 1) shareApi.track(shareId, data.sessionId, t.tab, secs);
      t.since = Date.now();
    };
    flush();
    tick.current = { tab, since: Date.now() };
    const iv = setInterval(flush, 20000);
    const onHide = () => { if (document.visibilityState === "hidden") flush(); else tick.current.since = Date.now(); };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", flush);
    return () => { flush(); clearInterval(iv); document.removeEventListener("visibilitychange", onHide); window.removeEventListener("pagehide", flush); };
  }, [tab, data?.sessionId, shareId]);

  if (!data) {
    return (
      <div className="invroom-gate" data-testid="invroom-gate">
        <div className="ig-card">
          <Logo height={44} testid="invroom-logo" />
          <div className="ig-brand">XYZ Ventures</div>
          <div className="ig-title">Investor Deal Room</div>
          {opening ? (
            <div className="ig-sub" data-testid="invroom-opening">Opening your secure deal room…</div>
          ) : (
            <>
              <div className="ig-sub">Enter your details and the 6-character passcode you received to view the confidential underwriting.</div>
              <div className="field"><label>Your name</label><input className="inp" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Investor" data-testid="invroom-name" /></div>
              <div className="field"><label>Email</label><input className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@fund.com" data-testid="invroom-email" /></div>
              <div className="ig-input">
                <Lock size={14} />
                <input className="inp mono" maxLength={6} placeholder="ABC123" value={pass}
                  onChange={(e) => setPass(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && enter()} data-testid="invroom-pass" />
              </div>
              {err && <div className="ig-err" data-testid="invroom-err">{err}</div>}
              <button className="btn btn-primary" style={{ width: "100%", marginTop: 12 }} onClick={() => enter()} disabled={busy || pass.length < 6} data-testid="invroom-enter">
                {busy ? "Opening…" : "Open deal room"}
              </button>
              <div className="ig-foot">Confidential — for the named recipient only.</div>
            </>
          )}
          <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}><ThemeToggle compact testid="gate-theme" /></div>
        </div>
      </div>
    );
  }

  // One derivation of "what the investor sees" (lib/roomModel.js) — the admin-side reconciliation
  // card on the Deal Room page uses the exact same function, so the two views can never drift.
  const rm = roomModel(data, (data.snapshot && data.snapshot.M) || {});
  // The published snapshot is the base case; the investor can flex it without changing what was published.
  const M = rcase === "base" ? rm.M : m.scenarioModel(rm.M, rcase);
  const market = (data.snapshot && data.snapshot.market) || null;
  const platform = (data.snapshot && data.snapshot.platform) || null;
  const ownPlatform = (data.snapshot && data.snapshot.ownPlatform) || null;
  const blind = !!data.blind;
  const title = data.name || M.dealName || "Investment Opportunity";
  // Sources & Uses became its own section: rooms published before that keep it on wherever Financials is on.
  const secIds = data.sections.includes("sourcesuses") || !data.sections.includes("financials")
    ? data.sections : [...data.sections, "sourcesuses"];
  const tabs = ROOM_SECTIONS.filter((s) => secIds.includes(s.id));
  const v = m.verdict(M);
  const cov = m.coverage(M), dscr = cov.dscr, wf = m.lpWaterfall(M);
  const bump = qsbsBump(M);
  const ask = data.equityAsk > 0 ? data.equityAsk : m.lpAskOf(M);
  const updated = data.updatedAt ? new Date(data.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;
  const showInvest = data.sections.includes("invest");

  const acceptDisclaimer = () => {
    localStorage.setItem(disclaimerKey(shareId, email || name), new Date().toISOString());
    setNeedsDisclaimer(false);
    shareApi.accept(shareId, data.sessionId).catch(() => {});
  };

  return (
    <div className="invroom shell" data-testid="investor-room">
      {needsDisclaimer && <DisclaimerModal investor={name} onAccept={acceptDisclaimer} />}
      {printing && <PrintDeck data={data} M={M} title={title} platform={platform} market={market} ask={ask} investor={name} onDone={() => setPrinting(false)} />}
      {data.viewer?.staff && (
        <div className="ir-staff-bar" data-testid="ir-staff-preview">
          Staff preview — you are seeing exactly what an investor sees. Commitments and passes are disabled in preview.
        </div>
      )}
      <header className="ir-top">
        <div className="ir-top-l">
          <Logo height={34} testid="ir-logo" />
          <div>
            <div className="ir-brand">XYZ Ventures · Investor Deal Room</div>
            <div className="ir-deal" data-testid="ir-title">{title}{platform && <span className="ir-addon-badge" data-testid="ir-addon-badge"><GitMerge size={11} /> Add-on to {platform.name}</span>}</div>
            <div className="ir-meta">
              <span>{[M.industry || M.businessType, !blind ? M.address : null].filter(Boolean).join(" · ")}</span>
              {updated && <span className="ir-live" data-testid="ir-updated"><RefreshCw size={10} /> Live model · updated {updated}</span>}
            </div>
          </div>
        </div>
        <div className="ir-top-r">
          <div className="ir-stat"><span>The Ask</span><b className="mono" data-testid="ir-head-ask">{m.fmt$(ask)}</b></div>
          <div className="ir-stat"><span>DSCR Y{cov.year}</span><b className={`mono ${m.dscrTone(dscr) === "go" ? "pos" : m.dscrTone(dscr) === "pass" ? "neg" : ""}`}>{m.fmtX(dscr)}</b></div>
          <div className="ir-stat"><span>LP MOIC</span><b className="mono">{m.fmtX(wf.lpMoic)}</b>
            {bump && <i className="ir-bump" data-testid="ir-head-moic-bump">QSBS +{m.fmtX(bump.x)} after tax</i>}</div>
          <div className="ir-stat"><span>LP IRR</span><b className="mono">{isNaN(wf.lpIrr) ? "n/a" : m.fmtPct(wf.lpIrr)}</b>
            {bump && bump.bps > 0 && <i className="ir-bump" data-testid="ir-head-irr-bump">QSBS +{bump.bps} bps after tax</i>}</div>
          <RecBadge verdict={v} testid="ir-verdict" />
          {name && <span className="ir-viewer" data-testid="ir-viewer">Viewing as <b>{name}</b></span>}
          <button className="btn btn-sm ir-pdf-btn" onClick={() => setPrinting(true)} title="Download this deal room as a PDF pitchbook" data-testid="ir-download-pdf"><FileDown size={12} style={{ marginRight: 4 }} />PDF</button>
          <ThemeToggle compact testid="ir-theme" />
        </div>
      </header>
      {m.isRollupScope(M) && (
        <div className="ir-scope-bar" data-testid="ir-view-badge">
          <Layers size={15} strokeWidth={2} />
          <b>{rm.scopeLong.replace(/\+\s*\d+\s*add-ons?/i, "+ add-ons")}</b>
        </div>
      )}
      {showInvest && (
        <div className="ir-raise-strip" data-testid="ir-raise-strip">
          <CommitBar ask={ask} committed={data.commits?.total || 0} count={data.commits?.count || 0} testid="ir-strip-bar" />
          {tab !== "invest" && <button className="btn btn-sm btn-share" onClick={() => setTab("invest")} data-testid="ir-goto-invest">Commit equity →</button>}
        </div>
      )}

      <div className="ir-shell" style={printing ? { display: "none" } : undefined}>
        <aside className="ir-side" data-testid="ir-nav">
          {ROOM_GROUPS.map((g) => {
            const items = tabs.filter((t) => (t.group || "Decide") === g);
            if (!items.length) return null;
            return (
              <div className="ir-navgroup" key={g}>
                <span className="ir-navgroup-h">{g}</span>
                {items.map((t) => {
                  const Icon = SEC_ICON[t.id] || Layers;
                  return (
                    <button key={t.id} className={`ir-navlink ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)} data-testid={`ir-tab-${t.id}`}>
                      <span className="ir-navicon"><Icon size={15} strokeWidth={2} /></span>
                      <span className="ir-navtext">{t.label}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
          <div className="ir-case" data-testid="ir-case">
            <span>Stress the case</span>
            <div className="scen-bar scen-bar-sm">
              {[["downside", "Downside"], ["base", "Base"], ["upside", "Upside"]].map(([id, label]) => (
                <button key={id} className={rcase === id ? "active" : ""} onClick={() => setRcase(id)} data-testid={`ir-case-${id}`}>{label}</button>
              ))}
            </div>
            <i>{rcase === "base" ? "As published by the sponsor." : `Your own ${rcase} flex — revenue, margin and exit multiple stressed. Every return on this page updates.`}</i>
          </div>
          <div className="ir-side-foot">
            <span>Questions?</span>
            {M.analystContact && <b>{M.analystContact}</b>}
            {M.analystEmail && <a href={`mailto:${M.analystEmail}`}>{M.analystEmail}</a>}
          </div>
        </aside>

        <main className="ir-body" data-testid="ir-body">
          {tab === "overview" && <OpportunityTab M={M} title={title} platform={platform} market={market} thesis={data.thesis || {}} ask={ask} showInvest={showInvest} onInvest={() => setTab("invest")} minCommit={data.minCommit || 25000} classes={data.classes || []} timeline={data.timeline || []} firm={(data.snapshot && data.snapshot.firm) || null} own={ownPlatform} />}
          {tab === "thesis" && <ThesisTab M={M} thesis={data.thesis || {}} platform={platform} own={ownPlatform} />}
          {tab === "pipeline" && <PipelineTab M={M} pipeline={data.pipeline || []} />}
          {tab === "team" && <TeamTab team={(data.team && data.team.length ? data.team : (data.snapshot && data.snapshot.firm && data.snapshot.firm.team) || [])} firm={(data.snapshot && data.snapshot.firm) || null} M={M} />}
          {tab === "assumptions" && <StructureTab M={M} blind={blind} classes={data.classes || []} timeline={data.timeline || []} ask={ask} thesis={data.thesis || {}} firm={(data.snapshot && data.snapshot.firm) || null} />}
          {(tab === "invest" || tab === "pass") && <DecideSwitch tab={tab} onChange={setTab} hasInvest={showInvest} hasPass={data.sections.includes("pass")} />}
          {tab === "pass" && <div className="ir-decide-pane" key="pass"><PassTab shareId={shareId} sessionId={data.sessionId} investor={{ name, email }} title={title} onInvest={showInvest ? () => setTab("invest") : null} /></div>}
          {tab === "invest" && <div className="ir-decide-pane" key="invest"><InvestTab M={M} market={market} shareId={shareId} sessionId={data.sessionId} investor={{ name, email }} room={data} onCommits={(c) => setData((d) => ({ ...d, commits: c }))} /></div>}
          {tab === "qa" && <QATab shareId={shareId} sessionId={data.sessionId} investor={{ name, email }} />}
          {tab === "documents" && <DocumentsTab shareId={shareId} sessionId={data.sessionId} docs={data.docs} />}
          {tab === "financials" && <FinancialsTab M={M} />}
          {tab === "sourcesuses" && <SourcesUsesTab M={M} />}
          {tab === "returns" && <ReturnsTab M={M} />}
          {tab === "sensitivity" && <SensitivityTab M={M} />}
          {tab === "market" && <MarketTab market={market} thesis={data.thesis || {}} />}
          {tab === "risks" && <RisksTab M={M} />}
          {tab === "rollup" && (
            <div className="ir-readonly">
              <RollUpTab M={M} />
              <div className="ir-gap"><BackLeverCard M={M} V={M} patch={() => {}} readOnly /></div>
              <div className="ir-gap"><RollUpEconomics B={(M.rollup && M.rollup.backLever) || m.DEFAULT_BACKLEVER} onChange={() => {}} live={(() => { const wf = m.lpWaterfall(M); return { equity: wf.lpEquity, toInvestors: wf.lpTotal, exitYear: m.effectiveHold(M) }; })()} lev={m.levCompare(M)} readOnly /></div>
            </div>
          )}
        </main>
      </div>

      <footer className="ir-foot">
        <span>XYZ Ventures · Confidential — prepared for {name || "the named recipient"} only. Illustrative projections; not an offer to sell securities.</span>
        {M.analystContact && <span className="ir-contact">Contact: <b>{M.analystContact}</b>{M.analystEmail ? ` · ${M.analystEmail}` : ""}{M.analystPhone ? ` · ${M.analystPhone}` : ""}</span>}
      </footer>
    </div>
  );
}
